import { FormErrors } from '../../../KnmPackage/MainModule/types/FormErrors';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { KnmInvalidFieldEnum } from '../../../KnmPackage/MainModule/enums/KnmInvalidFieldEnum';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { IKnmErknmFormInspector } from '../interfaces/IKnmErknmFormInspector';
import { IKnmErknmFormExpert } from '../interfaces/IKnmErknmFormExpert';
import { IKnmErknmFormObject } from '../interfaces/IKnmErknmFormObject';
import { IKnmErknmFormReason } from '../interfaces/IKnmErknmFormReason';
import { IKnmErknmFormCollaboratingOrganization } from '../../../KnmPackage/MainModule/interfaces/IKnmErknmFormCollaboratingOrganization';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormRequirementNpa } from '../interfaces/IKnmErknmFormRequirementNpa';
import { IKnmErknmFormDocTarget } from '../interfaces/IKnmErknmFormDocTarget';
import { IKnmErknmFormChecklist } from '../interfaces/IKnmErknmFormChecklist';
import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';

const INVALID_FIELD_ERROR = 'Сведения устарели в системе-источнике данных';

export const isInvalidField = (
  fieldTemplate: KnmInvalidFieldEnum,
  invalidFields: string[],
  ...guidList: string[]
): boolean => {
  let field: string = fieldTemplate;

  if (fieldTemplate.includes('{id}') && guidList.length) {
    guidList.forEach(guid => {
      field = field.replace('{id}', guid);
    });
  }

  return invalidFields.includes(field);
};

const isInitialValue = (
  currentValue: IDictionarySelectOption<{}> | undefined,
  initialValue: IDictionarySelectOption<{}> | undefined,
): boolean => {
  return !!currentValue?.value.dictVersionId && currentValue.value.dictVersionId === initialValue?.value.dictVersionId;
};

const getReasonErvkUuid = (reason: IKnmErknmFormReason | undefined): string | undefined | null => {
  return reason?.reason?.ervkUuid || reason?.type?.ervkUuid;
};

const getRequirementNpaId = (requirementsNpa: IKnmErknmFormRequirementNpa): string => {
  const npaId = requirementsNpa.npa.id;
  const structuralUnitId = requirementsNpa.structuralUnit.id;

  return `${npaId}|${structuralUnitId}`;
};

/*
  Валидация для подсветки полей с неактуальными справочными значениями
*/
export const validateInvalidFields = (
  values: IKnmForm,
  initialFormValues: IKnmErknmForm | undefined,
  isNewKnm: boolean | undefined,
  isKnmStatusTypeCarriedOut: boolean | undefined,
  isKnmStatusTypeCompleted: boolean | undefined,
  created: string | null | undefined,
  settings: IAdminSetting[] | undefined,
): FormErrors<IKnmForm> => {
  const {
    knoOrganizationErknm,
    kindControl,
    kindKnm,
    typeErknm,
    invalidFields,
    objectsErknm,
    organizations,
    prosecutorOrganizationErknm,
    inspectorsErknm,
    experts,
    reasonsErknm,
    collaboratingOrganizationsErknm,
    eventsErknm,
    requirementsNpaErknm,
    docTarget,
    checklistsErknm,
  } = values;

  if (!invalidFields || !initialFormValues) {
    return {};
  }

  const initialKnoOrganizationErknm = initialFormValues.knoOrganizationErknm;
  const initialKindControl = initialFormValues.kindControl;
  const initialKindKnm = initialFormValues.kindKnm;
  const initialTypeErknm = initialFormValues.typeErknm;
  const initialObjectsErknm = initialFormValues.objectsErknm;
  const initialOrganizations = initialFormValues.organizations;
  const initialProsecutorOrganizationErknm = initialFormValues.prosecutorOrganizationErknm;
  const initialInspectorsErknm = initialFormValues.inspectorsErknm;
  const initialExperts = initialFormValues.experts;
  const initialReasonsErknm = initialFormValues.reasonsErknm;
  const initialCollaboratingOrganizationsErknm = initialFormValues.collaboratingOrganizationsErknm;
  const initialEventsErknm = initialFormValues.eventsErknm;
  const initialDocTarget = initialFormValues.docTarget;
  const initialChecklistsErknm = initialFormValues.checklistsErknm;

  const isLaterThanNpaValidationSetting: boolean = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_STARTS_FROM,
  );

  const result = {
    knoOrganizationErknm:
      isInitialValue(knoOrganizationErknm, initialKnoOrganizationErknm) &&
      isInvalidField(KnmInvalidFieldEnum.KNO_ORGANIZATION_ERKNM, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    kindControl:
      isInitialValue(kindControl, initialKindControl) && isInvalidField(KnmInvalidFieldEnum.KIND_CONTROL, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    kindKnm:
      isInitialValue(kindKnm, initialKindKnm) && isInvalidField(KnmInvalidFieldEnum.KIND_KNM, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    typeErknm:
      typeErknm &&
      typeErknm.value === initialTypeErknm?.value &&
      isInvalidField(KnmInvalidFieldEnum.TYPE_ERKNM, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    reasonsErknm: reasonsErknm?.map(
      (item): FormErrors<IKnmErknmFormReason> => {
        const initialItem = initialReasonsErknm?.find(
          initialReason => item.numGuid && item.numGuid === initialReason.numGuid,
        );
        const itemErvkUuid = getReasonErvkUuid(item);
        const initialItemErvkUuid = getReasonErvkUuid(initialItem);

        return {
          type:
            itemErvkUuid &&
            itemErvkUuid === initialItemErvkUuid &&
            isInvalidField(KnmInvalidFieldEnum.REASONS_ERKNM_ERVK_UUID, invalidFields, item.numGuid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
          assignmentNumber:
            !isNewKnm &&
            item.order?.recordVersionId &&
            item.order.recordVersionId === initialItem?.order?.recordVersionId &&
            isInvalidField(KnmInvalidFieldEnum.REASONS_ERKNM_ORDER, invalidFields, item.numGuid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
          riskIndicators: item.riskIndicators?.map(
            (riskIndicatorItem): FormErrors<IKnmFormRiskIndicators> => {
              const initialRiskIndicatorItem = initialItem?.riskIndicators?.find(
                initialRiskIndicator => riskIndicatorItem.guid && riskIndicatorItem.guid === initialRiskIndicator.guid,
              );

              return {
                riskIndicator:
                  !isNewKnm &&
                  isInitialValue(riskIndicatorItem.riskIndicator, initialRiskIndicatorItem?.riskIndicator) &&
                  (isInvalidField(
                    KnmInvalidFieldEnum.REASONS_ERKNM_RISK_INDICATORS,
                    invalidFields,
                    item.numGuid || '',
                  ) ||
                    isInvalidField(
                      KnmInvalidFieldEnum.REASONS_ERKNM_RISK_INDICATOR,
                      invalidFields,
                      item.numGuid || '',
                      riskIndicatorItem.guid,
                    ))
                    ? INVALID_FIELD_ERROR
                    : undefined,
              };
            },
          ),
        };
      },
    ),
    prosecutorOrganizationErknm:
      prosecutorOrganizationErknm &&
      prosecutorOrganizationErknm.id === initialProsecutorOrganizationErknm?.id &&
      isInvalidField(KnmInvalidFieldEnum.PROSECUTOR_ORGANIZATION_ERKNM, invalidFields)
        ? INVALID_FIELD_ERROR
        : undefined,
    objectsErknm: (() => {
      const resultObjectsErknm = objectsErknm?.map(
        (item): FormErrors<IKnmErknmFormObject> => {
          const initialItem = initialObjectsErknm?.find(initialObject => item.guid && item.guid === initialObject.guid);

          return {
            name:
              item.ervkId &&
              item.ervkId === initialItem?.ervkId &&
              isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_ERVK_ID, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
            objectType:
              isInitialValue(item.objectType, initialItem?.objectType) &&
              isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_OBJECT_TYPE, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
            objectKind:
              isInitialValue(item.objectKind, initialItem?.objectKind) &&
              isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_OBJECT_KIND, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
            objectSubKind:
              isInitialValue(item.objectSubKind, initialItem?.objectSubKind) &&
              isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_OBJECT_SUB_KIND, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
            riskCategory:
              isInitialValue(item.riskCategory, initialItem?.riskCategory) &&
              isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_RISK_CATEGORY, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
            dangerClass:
              isInitialValue(item.dangerClass, initialItem?.dangerClass) &&
              isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_DANGER_CLASS, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
          };
        },
      );

      return resultObjectsErknm?.length ? resultObjectsErknm : undefined;
    })(),
    organizations: (() => {
      const resultOrganizations = organizations?.map(
        (item): FormErrors<IKnmErknmFormSubject> => {
          const initialItem = initialOrganizations?.find(
            initialOrganization => item.guid && item.guid === initialOrganization.guid,
          );

          return {
            organizationName:
              item.ervkId &&
              item.ervkId === initialItem?.ervkId &&
              isInvalidField(KnmInvalidFieldEnum.ORGANIZATIONS_ERVK_ID, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
            subjectType:
              item.subjectType?.code &&
              item.subjectType.code === initialItem?.subjectType?.code &&
              isInvalidField(KnmInvalidFieldEnum.ORGANIZATIONS_SUBJECT_TYPE, invalidFields, item.guid || '')
                ? INVALID_FIELD_ERROR
                : undefined,
          };
        },
      );

      return resultOrganizations?.length ? resultOrganizations : undefined;
    })(),
    inspectorsErknm: inspectorsErknm?.map(
      (item): FormErrors<IKnmErknmFormInspector> => {
        const initialItem = initialInspectorsErknm?.find(
          initialInspector => item.guid && item.guid === initialInspector.guid,
        );

        return {
          position:
            isInitialValue(item.position, initialItem?.position) &&
            isInvalidField(KnmInvalidFieldEnum.INSPECTORS_ERKNM_POSITION, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
    experts: experts?.map(
      (item): FormErrors<IKnmErknmFormExpert> => {
        const initialItem = initialExperts?.find(initialExpert => item.guid && item.guid === initialExpert.guid);

        return {
          type:
            item.type &&
            item.type.id === initialItem?.type?.id &&
            isInvalidField(KnmInvalidFieldEnum.EXPERTS_TYPE, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
    collaboratingOrganizationsErknm: collaboratingOrganizationsErknm?.map(
      (item): FormErrors<IKnmErknmFormCollaboratingOrganization> => {
        const initialItem = initialCollaboratingOrganizationsErknm?.find(
          collaboratingOrganization => item.guid && item.guid === collaboratingOrganization.guid,
        );

        return {
          organization:
            !!item.guid &&
            item.guid === initialItem?.guid &&
            isInvalidField(
              KnmInvalidFieldEnum.COLLABORATING_ORGANIZATIONS_ITEM_ORGANIZATION,
              invalidFields,
              item.guid || '',
            )
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
    eventsErknm: eventsErknm?.map(
      (item): FormErrors<IKnmErknmFormEvent> => {
        const initialItem = initialEventsErknm?.find(initialEvent => item.guid && item.guid === initialEvent.guid);

        return {
          type:
            isInitialValue(item.type, initialItem?.type) &&
            isInvalidField(KnmInvalidFieldEnum.EVENTS_ERKNM_TYPE, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
    requirementsNpaErknm: requirementsNpaErknm?.map((item): FormErrors<IKnmErknmFormRequirementNpa> | {} => {
      const itemId = getRequirementNpaId(item);

      // После начала проведения КНМ соответствие КНО и ВК не проверяется, исправить раздел уже нельзя
      if (
        !isKnmStatusTypeCarriedOut &&
        !isKnmStatusTypeCompleted &&
        (item.hasInvalidExistNpa || item.hasInvalidStructureNpa || item.hasInvalidMandatoryNpa)
      ) {
        return { npa: 'Требование не соответствует выбранному ВК и КНО' };
      }

      if (isLaterThanNpaValidationSetting) {
        if (item.mandatory?.find(m => m.hasInvalidControlObjectFilter)) {
          return { npa: 'Требование не соответствует выбранным типу, виду и подвиду объекта контроля' };
        }

        if (item.mandatory?.find(m => m.hasInvalidRiscIndicatorsFilter)) {
          return { npa: 'Требование не соответствует выбранному индикатору риска' };
        }
      }

      return isInvalidField(KnmInvalidFieldEnum.REQUIREMENTS_NPA_ERKNM_NPA_ID, invalidFields, itemId) ||
        isInvalidField(KnmInvalidFieldEnum.REQUIREMENTS_NPA_ERKNM_STRUCTURAL_UNIT_ID, invalidFields, itemId)
        ? { npa: INVALID_FIELD_ERROR }
        : {};
    }),
    docTarget: docTarget?.map(
      (item): FormErrors<IKnmErknmFormDocTarget> => {
        const initialItem = initialDocTarget?.find(doc => item.guid && item.guid === doc.guid);

        return {
          ervkControlKindDocTarget:
            item.ervkControlKindDocTarget?.targetId &&
            item.ervkControlKindDocTarget.targetId === initialItem?.ervkControlKindDocTarget?.targetId &&
            isInvalidField(KnmInvalidFieldEnum.DOC_TARGET_ERVK_CONTROL_KIND_DOC_TARGET, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
    checklistsErknm: checklistsErknm?.map(
      (item): FormErrors<IKnmErknmFormChecklist> => {
        const initialItem = initialChecklistsErknm?.find(
          initialChecklist => item.guid && item.guid === initialChecklist.guid,
        );

        return {
          checkList:
            isInitialValue(item.checkList, initialItem?.checkList) &&
            isInvalidField(KnmInvalidFieldEnum.CHECKLISTS_ERKNM_CHECKLIST, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
          ervkCheckList:
            typeof item.ervkCheckList?.number === 'number' &&
            item.ervkCheckList.number === initialItem?.ervkCheckList?.number &&
            isInvalidField(KnmInvalidFieldEnum.CHECKLISTS_ERKNM_ERVK_CHECKLIST, invalidFields, item.guid || '')
              ? INVALID_FIELD_ERROR
              : undefined,
        };
      },
    ),
  };

  return JSON.parse(JSON.stringify(result));
};
